import { useState, useRef, useEffect } from 'react';
import { useTokens } from '../../../contexts/TokenContext';
import InsufficientTokensModal from '../../../components/feature/InsufficientTokensModal';
import BirthSajuPanel, { type BirthSajuResult } from './BirthSajuPanel';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  tags?: string[];
}

type DecisionCategory = {
  id: string;
  label: string;
  icon: string;
  color: string;
};

const CATEGORIES: DecisionCategory[] = [
  { id: 'career', label: '직장 / 커리어', icon: 'ri-briefcase-4-line', color: 'from-amber-500 to-orange-600' },
  { id: 'love', label: '결혼 / 이별', icon: 'ri-heart-3-line', color: 'from-rose-500 to-pink-600' },
  { id: 'move', label: '이사 / 이민', icon: 'ri-map-pin-2-line', color: 'from-emerald-500 to-teal-600' },
  { id: 'business', label: '창업 / 투자', icon: 'ri-line-chart-line', color: 'from-violet-500 to-purple-600' },
  { id: 'study', label: '학업 / 유학', icon: 'ri-graduation-cap-line', color: 'from-sky-500 to-cyan-600' },
  { id: 'health', label: '건강 / 라이프', icon: 'ri-heart-pulse-line', color: 'from-green-500 to-lime-600' },
];

type DecisionAdvice = {
  timing: string;
  elements: string;
  risk: string;
  advice: string;
  luckyItems: string[];
  tags: string[];
};

const DECISION_DATABASE: Record<string, DecisionAdvice> = {
  career: {
    timing: '경금(庚金) · 신금(辛金) 운이 강한 가을(申酉月)이 직업 전환의 최적기입니다. 특히 경자·임인년 대운과 맞물리면 크게 도약할 수 있는 시기입니다.',
    elements: '사주에서 금(金) 기운이 강하면 리더십과 결단력이 뛰어나 새로운 도전에 유리합니다. 목(木) 기운이 강하면 창의적 분야, 수(水) 기운이 강하면 분석적 전문직이 잘 맞습니다.',
    risk: '현재 대운이 관살(官殺)이 강한 시기라면 이직보다 현 직장 내 승진을 노리는 것이 안전합니다. 충(沖)이 발생하는 해에는 무리한 결정을 피하세요.',
    advice: '직장 전환을 결정하기 전, 반드시 금전운과 관운이 모두 상승하는 시기를 택하세요. 단순히 좋은 기회가 왔다고 바로 뛰어드는 것보다, 당신의 천간지지(天干地支)와 상생(相生)하는 방향인지 확인이 필요합니다.',
    luckyItems: ['금속 소품', '서쪽 방향 사무실', '흰색 명함'],
    tags: ['커리어', '타이밍', '직업운'],
  },
  love: {
    timing: '목(木) 기운이 강한 봄(寅卯月)과 화(火) 기운이 올라오는 초여름이 결혼 결정의 최길 시기입니다. 특히 일지(日支)와 합(合)이 되는 해는 반드시 주목하세요.',
    elements: '일간(日干)과 배우자의 일간이 상생(相生) 관계라면 최상의 궁합입니다. 수(水)와 목(木), 목(木)과 화(火), 화(火)와 토(土)는 생조 관계로 장기적 안정을 의미합니다.',
    risk: '형충(刑沖)이 발생하는 해나 시기에 결혼을 서두르면 갈등이 깊어질 수 있습니다. 특히 대운에서 공망(空亡)이 걸린 시기는 신중하게 판단하세요.',
    advice: '이별 결정도 마찬가지입니다. 충(沖)의 에너지가 극도로 강한 시기의 결정은 나중에 후회할 수 있습니다. 감정이 가라앉은 뒤 냉철하게 결정하세요.',
    luckyItems: ['붉은 실 팔찌', '남서쪽 방향', '쌍화점 접시'],
    tags: ['연애운', '결혼', '궁합'],
  },
  move: {
    timing: '이사와 이민은 토(土) 기운이 변화하는 시기인 환절기(辰戌丑未月)가 중요합니다. 특히 삼합(三合)이 이루어지는 방향으로의 이동은 크게 길합니다.',
    elements: '사주에 역마살(驛馬殺)이 있다면 이동·이민이 오히려 운을 열어주는 열쇠가 됩니다. 수(水) 기운이 강하면 바다 근처, 목(木) 기운이 강하면 숲·자연 환경이 생기를 돋웁니다.',
    risk: '본명성(本命星)과 충(沖)이 되는 방위로의 이동은 피하는 것이 상책입니다. 예를 들어 자(子)띠는 오(午) 방향인 정남쪽으로의 이사를 신중히 고려하세요.',
    advice: '이민은 단순한 거주지 이동이 아니라 전체 운의 흐름을 바꾸는 결정입니다. 새 거주지의 품수(風水)와 당신의 명조(命造)가 상생하는지 반드시 확인하고, 출발일도 길일(吉日)을 택하세요.',
    luckyItems: ['새 집 황토색 소품', '북동 방향', '오동나무 물건'],
    tags: ['이사운', '방위', '변화'],
  },
  business: {
    timing: '창업은 목(木)이 강한 봄 또는 화(火)가 왕성한 여름이 에너지 충전에 유리합니다. 투자는 금(金) 기운이 강한 가을에 냉철한 판단력이 극대화됩니다.',
    elements: '사주에 식신(食神)과 재성(財星)이 동시에 존재하면 창업 적성이 최상입니다. 겁재(劫財)가 강하면 동업보다 단독 창업이 유리하고, 인성(印星)이 강하면 지식·콘텐츠 기반 사업이 맞습니다.',
    risk: '비겁(比劫)이 강한 해에는 동업 갈등과 배신 위험이 높습니다. 관살(官殺)이 충(沖)을 받는 시기의 창업은 법적 분쟁으로 이어질 수 있으니 계약서를 꼼꼼히 검토하세요.',
    advice: '투자 결정은 반드시 재성(財星) 대운이 오는 시기에 맞춰야 합니다. 아무리 좋은 기회도 운이 뒷받침되지 않으면 손실로 이어집니다. 겁재(劫財)운에서의 단타 투자는 극도로 위험합니다.',
    luckyItems: ['노란색 지갑', '동남쪽 사무실', '황금 계산기'],
    tags: ['재물운', '창업', '투자'],
  },
  study: {
    timing: '수(水) 기운이 강한 겨울(亥子月)이 학업 집중력이 극대화되는 시기입니다. 합격운은 금(金)이 생수(生水)하는 가을부터 겨울이 최상입니다.',
    elements: '인성(印星)이 강한 명조는 학문적 재능이 탁월합니다. 갑목(甲木) 일간은 리더십 교육, 임수(壬水) 일간은 연구·분석직, 병화(丙火) 일간은 예술·공연 분야가 맞습니다.',
    risk: '식신(食神)이 충(沖)을 받는 해에는 시험운이 약해집니다. 비겁(比劫)이 지나치게 강하면 경쟁에서 실수할 수 있으니 자만을 경계하세요.',
    advice: '유학 결정은 역마살(驛馬殺)과 천마(天馬)가 활성화되는 시기가 가장 좋습니다. 당신의 사주 오행과 상생하는 방위의 나라를 선택하면 현지 적응이 빠릅니다.',
    luckyItems: ['파란색 필기구', '북쪽 방향 책상', '수정 문진'],
    tags: ['학업운', '합격', '성장'],
  },
  health: {
    timing: '건강 관련 결정(수술, 시술, 식이 변화)은 화(火) 기운이 안정된 봄·가을이 적합합니다. 특히 형충(刑沖)이 없는 길일을 반드시 선택하세요.',
    elements: '목(木) 기운이 부족한 명조는 간·담·눈 건강에 주의하고, 금(金) 기운 과다는 폐·장·피부를 챙겨야 합니다. 수(水) 기운이 약하면 신장·방광·뼈 건강이 취약점입니다.',
    risk: '대운에서 충(沖)이나 형(刑)이 발생하는 해는 건강 문제가 불거질 가능성이 높습니다. 무리한 다이어트나 수술은 길일(吉日)에 맞춰 진행하세요.',
    advice: '라이프스타일 변화는 서서히, 사주에서 부족한 오행을 보완하는 방향으로 바꾸세요. 수(水)가 약하면 수분 섭취와 수영이, 목(木)이 약하면 숲 산책과 채식이 좋습니다.',
    luckyItems: ['녹색 식물', '동쪽 방향 침대 배치', '천연 허브'],
    tags: ['건강운', '라이프', '균형'],
  },
};

const DAY_STEM_ADVICE: Record<string, Record<string, string>> = {
  career: {
    갑: '갑목(甲木) 일간은 선구자·개척자 기질이 강합니다. 창업이나 새로운 프로젝트 리더로서 봄(寅卯月)에 출발하면 최대 에너지가 발휘됩니다.',
    을: '을목(乙木) 일간은 네트워킹과 유연한 적응력이 무기입니다. 좋은 귀인을 만나는 시기에 이직·전환을 결정하면 시너지가 폭발합니다.',
    병: '병화(丙火) 일간은 무대와 대중을 상대할 때 빛납니다. 발표·영업·강의처럼 사람 앞에 서는 커리어에서 강운이 발동합니다.',
    정: '정화(丁火) 일간은 전문성 축적이 핵심 전략입니다. 하나의 분야를 10년 이상 깊이 파면 만기필성(晩期必成)의 결과가 반드시 옵니다.',
    무: '무토(戊土) 일간은 신뢰와 안정감이 최고의 자산입니다. 조직 내 중추 역할이나 중재·관리직에서 장기적 성공이 보장됩니다.',
    기: '기토(己土) 일간은 실속과 내실을 다지는 타입입니다. 화려함보다 꾸준한 성과 축적이 결국 크게 빛납니다. 교육·서비스·유통업이 잘 맞습니다.',
    경: '경금(庚金) 일간은 결단력과 추진력이 탁월합니다. 목기운(木) 대운이 올 때 커리어 최대 도약의 기회가 찾아옵니다.',
    신: '신금(辛金) 일간은 섬세함과 완벽주의가 강점입니다. 예술·뷰티·정밀 분야 또는 수(水) 대운 시기의 이직이 가장 성공 확률이 높습니다.',
    임: '임수(壬水) 일간은 전략적 사고와 큰 그림 구상이 특기입니다. 금융·기획·컨설팅에서 화(火) 대운이 올 때 재물과 명예가 동시에 상승합니다.',
    계: '계수(癸水) 일간은 감성 지능과 직관력이 뛰어납니다. 콘텐츠·상담·치유 분야에서 봄(木運)이 도래하면 감성이 실력으로 꽃을 피웁니다.',
  },
  love: {
    갑: '갑목 일간은 파트너에게 지지받고 싶어하는 내면이 있습니다. 수(水) 오행 기운의 파트너와 상생이 깊고, 봄 시기 만남이 큰 인연으로 발전합니다.',
    을: '을목 일간은 감성적 연결을 중시합니다. 화(火) 기운의 열정적인 파트너와 잘 맞으며, 강요보다 자유로운 관계 구조에서 사랑이 성장합니다.',
    병: '병화 일간은 감정 표현이 솔직하고 열정적입니다. 토(土) 기운의 안정적인 파트너가 장기적 안정을 가져다주며, 여름 시기 결정이 길합니다.',
    정: '정화 일간은 깊고 헌신적인 사랑을 추구합니다. 목(木) 기운의 지지해주는 파트너와 결합할 때 불꽃이 오래 타오릅니다.',
    무: '무토 일간은 포용적이고 안정 지향적입니다. 화(火) 기운의 원칙적인 파트너가 무토에게 생기를 불어넣어 주며, 자연스럽게 부부운이 열립니다.',
    기: '기토 일간은 현실적인 사랑을 추구합니다. 화(火) 기운의 생동감 있는 파트너가 가장 잘 맞고, 안정된 환경에서 결혼 결정을 내리는 게 최상입니다.',
    경: '경금 일간은 결혼에서도 결단력이 중요합니다. 토(土) 기운의 묵직하고 신뢰감 있는 파트너와 깊은 인연을 맺으며, 가을 시기 결정이 길합니다.',
    신: '신금 일간은 섬세한 감수성으로 상대방을 배려합니다. 수(水) 기운의 지혜로운 파트너와 세련된 관계를 형성하며 장기 안정을 이룹니다.',
    임: '임수 일간은 자유롭고 넓은 관계를 원합니다. 금(金) 기운의 원칙적인 파트너가 임수를 잡아주는 역할을 하며, 봄 시기 만남이 대인연입니다.',
    계: '계수 일간은 감성적 공감 능력이 뛰어납니다. 목(木) 기운의 따뜻하고 성장 지향적인 파트너와 서로 성장하는 관계를 이룹니다.',
  },
};

function buildPersonalizedResponse(
  userMsg: string,
  category: string,
  sajuResult: BirthSajuResult | null,
): { content: string; tags: string[] } {
  const data = DECISION_DATABASE[category] || DECISION_DATABASE['career'];
  const categoryLabel = CATEGORIES.find((c) => c.id === category)?.label || '결정';

  if (!sajuResult) {
    return { content: [
      categoryLabel + ' 관련 중대한 결정을 앞두고 계시는군요. 평생 운세 데이터를 기반으로 종합 분석해드리겠습니다.',
      '',
      '⏰ 최적 타이밍 분석',
      data.timing,
      '',
      '☯️ 오행(五行) 관점',
      data.elements,
      '',
      '⚠️ 주의할 시기와 리스크',
      data.risk,
      '',
      '💎 Destiny GPT 핵심 조언',
      data.advice,
      '',
      '🍀 길(吉)을 부르는 아이템',
      data.luckyItems.map((item) => '• ' + item).join('\n'),
      '',
      '📌 생년월일을 입력하시면 일간(日干) 기반 완전 개인 맞춤 분석을 제공해드립니다.',
    ].join('\n'), tags: data.tags };
  }

  const personalAdvice =
    DAY_STEM_ADVICE[category]?.[sajuResult.dayStem] ||
    `${sajuResult.dayStem}${sajuResult.dayBranch} 일주 기준으로 ${data.elements}`;

  const strongestLabel = sajuResult.strongest;
  const weakestLabel = sajuResult.weakest;

  const dominantGodsText = sajuResult.dominantGods.length > 0
    ? `사주 십신 구성: ${sajuResult.dominantGods.map((g) => `${g.god}(${g.shortMeaning})`).join(' · ')}`
    : '';

  const lines = [
    `${sajuResult.dayStem}${sajuResult.dayBranch} 일주의 ${categoryLabel} 결정 — 개인 맞춤 분석입니다.`,
    '',
    `🔮 내 일간(日干) 분석 · ${sajuResult.dayStem}${sajuResult.dayBranch}`,
    personalAdvice,
    '',
    `🌀 내 오행 분포 기반 조언`,
    `당신의 사주에서 ${strongestLabel}이 가장 강하고 ${weakestLabel}이 부족합니다.`,
    sajuResult.description,
    dominantGodsText ? '\n' + dominantGodsText : '',
    '',
    '⏰ 최적 타이밍 분석',
    data.timing,
    '',
    '⚠️ 주의할 시기와 리스크',
    data.risk,
    '',
    '💎 핵심 조언',
    data.advice,
    '',
    '🍀 길(吉)을 부르는 아이템',
    data.luckyItems.map((item) => '• ' + item).join('\n'),
  ];

  return {
    content: lines.filter((l) => l !== undefined).join('\n'),
    tags: [...data.tags, sajuResult.dayStem + '일간', sajuResult.strongest.replace('(', '').replace(')', '') + '기운'],
  };
}

function buildFollowUpResponse(userMsg: string): { content: string; tags: string[] } {
  const lower = userMsg.toLowerCase();

  if (lower.includes('생년') || lower.includes('년') || /\d{4}/.test(userMsg)) {
    const lines = [
      '생년월일 정보 감사합니다! 보다 정밀한 사주 분석을 진행하겠습니다.',
      '',
      '🔮 개인 명조 분석',
      '입력하신 생년월일을 기반으로 일간(日干)과 대운(大運) 흐름을 계산합니다. 현재 당신의 대운은 큰 변화와 새로운 시작을 예고하는 흐름에 놓여 있습니다.',
      '',
      '📊 현재 운세 상태',
      '• 재물운: 상승 기류 (★★★★☆)',
      '• 관운(직업/명예): 안정적 흐름 (★★★★☆)',
      '• 대인운: 귀인 출현 가능성 (★★★★★)',
      '• 건강운: 주의 필요 (★★★☆☆)',
      '',
      '💡 최종 결정 조언',
      '지금 당신의 운세는 결정의 시기에 놓여 있습니다. 두려움보다 확신을 갖고 전진하되, 시기 선택에서만큼은 신중히 하세요. 2025~2026년이 당신에게 가장 강력한 변화의 창(Window)입니다.',
    ];
    return { content: lines.join('\n'), tags: ['개인분석', '대운', '맞춤운세'] };
  }

  if (lower.includes('언제') || lower.includes('시기') || lower.includes('타이밍')) {
    const lines = [
      '타이밍에 대해 더 구체적으로 분석해드릴게요.',
      '',
      '📅 2025~2026년 주요 길일(吉日) 포인트',
      '• 2025년 3~4월: 목(木) 기운 상승, 시작·도전에 최상',
      '• 2025년 9~10월: 금(金) 결단력 극대화, 계약·투자 결정에 유리',
      '• 2026년 1~2월: 수(水) 지혜 충만, 중요한 공부·계획 수립에 좋음',
      '• 2026년 6~7월: 화(火) 에너지 폭발, 새 사업·발표·퍼블릭 무대 최적',
      '',
      '⚡ 피해야 할 시기',
      '충(沖)이 강한 해에 일어나는 충동적 결정, 감정적 고조기(7~8월)의 중요 계약, 공망(空亡)이 걸린 날의 출발은 지양하세요.',
      '',
      '결정하려는 구체적인 날짜가 있으신가요? 해당 날짜의 천간지지(天干地支)를 확인해드릴 수 있습니다.',
    ];
    return { content: lines.join('\n'), tags: ['타이밍', '길일', '2025운세'] };
  }

  if (lower.includes('위험') || lower.includes('실패') || lower.includes('두럽') || lower.includes('걱정')) {
    const lines = [
      '결정에 앞선 두려움과 불안, 충분히 이해합니다. 운세적 관점에서 리스크를 분석해드릴게요.',
      '',
      '🛡️ 리스크 분석',
      '모든 중대한 결정에는 리스크가 따릅니다. 하지만 사주에서 관살(官殺)이 인성(印星)으로 화(化)한다는 말처럼, 위기는 관리할 수 있을 때 기회가 됩니다.',
      '',
      '🔑 두려움을 이기는 운세 법칙',
      '1. 결정을 미루는 것도 결정입니다 — 운의 흐름을 놓치면 더 큰 손실',
      '2. 최악의 시나리오를 미리 준비하면 충(沖)의 기운도 반감됩니다',
      '3. 귀인(貴人)에게 조언을 구하는 것이 인성(印星)의 지혜입니다',
      '',
      '지금 가장 걱정되는 부분이 무엇인지 구체적으로 말씀해주시면 더 정확한 운세 조언을 드릴 수 있어요.',
    ];
    return { content: lines.join('\n'), tags: ['리스크', '두려움', '결단'] };
  }

  const lines = [
    '말씀해주신 내용을 바탕으로 추가 분석을 진행하겠습니다.',
    '',
    '🌟 운세 기반 추가 조언',
    '중대한 결정일수록 세 가지를 항상 확인하세요:',
    '',
    '1. 시기(時期) — 아무리 옳은 결정도 때를 잘못 택하면 힘들어집니다',
    '2. 방향(方向) — 당신의 오행과 상생하는 방향으로 나아가야 에너지 소모가 적습니다',
    '3. 사람(貴人) — 이 결정에서 당신을 도와줄 귀인(貴人)이 누군지 파악하세요',
    '',
    '더 구체적인 상황을 알려주시거나, 생년월일을 공유해주시면 개인 맞춤 분석이 가능합니다. 어떤 부분이 가장 고민이신가요?',
  ];
  return { content: lines.join('\n'), tags: ['운세조언', '결단력', '귀인'] };
}

export default function DestinyGPTChat() {
  const { tokens, deductTokens, hasEnoughTokens } = useTokens();
  const [sajuResult, setSajuResult] = useState<BirthSajuResult | null>(null);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 1,
      role: 'assistant',
      content: [
        '안녕하세요. 저는 Destiny GPT — 평생 운세 기반 중대 결정 비서입니다.',
        '',
        '인생에서 가장 중요한 순간, 직장 전환·결혼·이사·창업·투자 등 어떤 결정 앞에서도 당신의 사주(四柱)와 운세 흐름을 분석해 최선의 방향을 제시합니다.',
        '',
        '📌 생년월일을 먼저 입력하시면 실제 사주 오행을 계산해 완전 개인 맞춤 분석을 제공합니다.',
        '',
        '아래 카테고리를 선택하거나 직접 고민을 말씀해주세요. 당신이 좋은 결정을 하길 바랍니다.',
      ].join('\n'),
      timestamp: new Date().toISOString(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [requiredTokens, setRequiredTokens] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const userMessageCount = messages.filter((m) => m.role === 'user').length;
  const usedTokens = userMessageCount * 8;
  const maxTokens = 500;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCategorySelect = (cat: DecisionCategory) => {
    setSelectedCategory(cat.id);
    const promptMap: Record<string, string> = {
      career: '직장 전환 또는 커리어 변화를 고민하고 있습니다. 운세로 봤을 때 지금이 좋은 시기인가요?',
      love: '결혼(또는 이별)을 결정해야 하는 순간입니다. 운세와 궁합 관점에서 조언해주세요.',
      move: '이사 또는 해외 이민을 고려 중입니다. 어떤 방향과 시기가 좋을까요?',
      business: '창업이나 투자를 앞두고 있습니다. 사주로 보면 지금 제 재물운과 사업운은 어떤가요?',
      study: '학업 방향 전환이나 유학을 결정해야 합니다. 운세로 보는 최적의 선택은 무엇인가요?',
      health: '건강이나 라이프스타일에 큰 변화를 주려고 합니다. 운세적으로 어떤 점을 주의해야 할까요?',
    };
    setInputValue(promptMap[cat.id] || '');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSend = () => {
    if (!inputValue.trim()) return;
    const MESSAGE_COST = 8;
    if (!hasEnoughTokens(MESSAGE_COST)) {
      setRequiredTokens(MESSAGE_COST);
      setShowInsufficientModal(true);
      return;
    }
    deductTokens(MESSAGE_COST);

    const userText = inputValue.trim();
    const userMessage: Message = {
      id: messages.length + 1,
      role: 'user',
      content: userText,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      const isFirst = messages.filter((m) => m.role === 'user').length === 0;
      let result: { content: string; tags: string[] };

      if (isFirst && selectedCategory) {
        result = buildPersonalizedResponse(userText, selectedCategory, sajuResult);
      } else {
        result = buildFollowUpResponse(userText);
      }

      const aiMessage: Message = {
        id: messages.length + 2,
        role: 'assistant',
        content: result.content,
        timestamp: new Date().toISOString(),
        tags: result.tags,
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }, 2200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto">
        {/* Birth Saju Panel */}
        <BirthSajuPanel onResult={setSajuResult} result={sajuResult} />

        {/* Category Selector */}
        <div className="mb-6">
          <p className="text-center text-purple-300/70 text-sm mb-4 tracking-wide uppercase">결정 카테고리 선택</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategorySelect(cat)}
                className={`group flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-300 cursor-pointer ${
                  selectedCategory === cat.id
                    ? 'bg-primary-gold/20 border-primary-gold text-primary-gold'
                    : 'bg-white/5 border-white/10 text-purple-200 hover:bg-white/10 hover:border-primary-gold/40 hover:text-white'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.color} flex-shrink-0`}>
                  <i className={`${cat.icon} text-white text-base`}></i>
                </div>
                <span className="text-sm font-semibold whitespace-nowrap">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Chat Box */}
        <div className="bg-gradient-to-br from-primary-dark/90 via-primary-dark/80 to-mystic-dark/90 backdrop-blur-xl rounded-3xl border-2 border-primary-gold/40 overflow-hidden shadow-[0_0_80px_rgba(255,215,0,0.12)]">
          {/* Header */}
          <div className="flex items-center gap-3 px-6 py-4 border-b border-primary-gold/20 bg-gradient-to-r from-primary-dark/60 to-mystic-dark/60">
            <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 shadow-lg shadow-amber-500/50">
              <i className="ri-scales-3-line text-primary-dark text-lg"></i>
            </div>
            <div>
              <p className="text-white text-sm font-bold tracking-wide">Destiny GPT · 중대 결정 비서</p>
              <p className="text-amber-400/60 text-xs">
                {sajuResult
                  ? `${sajuResult.dayStem}${sajuResult.dayBranch} 일주 · ${sajuResult.strongest} 기운 · 개인 맞춤 분석 활성화`
                  : '평생 운세 기반 · 사주 · 오행 · 타이밍 분석'}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30">
              <div className={`w-2 h-2 rounded-full ${sajuResult ? 'bg-emerald-400' : 'bg-amber-400'} animate-pulse`}></div>
              <span className={`${sajuResult ? 'text-emerald-400' : 'text-amber-400'} text-xs font-medium whitespace-nowrap`}>
                {sajuResult ? '맞춤 분석' : '온라인'}
              </span>
            </div>
          </div>

          {/* Messages */}
          <div className="h-[54vh] sm:h-[520px] overflow-y-auto p-4 sm:p-8 space-y-6 custom-scrollbar">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 sm:gap-4 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
              >
                <div
                  className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shadow-lg ${
                    message.role === 'assistant'
                      ? 'bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 shadow-amber-500/50'
                      : 'bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-700 shadow-purple-500/50'
                  }`}
                >
                  <i className={`${message.role === 'assistant' ? 'ri-scales-3-line text-primary-dark' : 'ri-user-3-fill text-white'} text-lg sm:text-xl`}></i>
                </div>

                <div className={`flex-1 max-w-[90%] sm:max-w-[80%] ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                  <div
                    className={`inline-block rounded-3xl px-5 sm:px-7 py-4 sm:py-5 shadow-xl transition-all duration-300 hover:scale-[1.01] ${
                      message.role === 'assistant'
                        ? 'bg-gradient-to-br from-primary-dark/95 via-mystic-dark/90 to-primary-dark/95 border-2 border-primary-gold/30 text-white'
                        : 'bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-700 text-white'
                    }`}
                  >
                    <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap font-medium">
                      {message.content}
                    </p>
                  </div>

                  {message.tags && message.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {message.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 rounded-full bg-primary-gold/10 border border-primary-gold/30 text-primary-gold text-xs font-medium">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/50 flex-shrink-0">
                  <i className="ri-scales-3-line text-primary-dark text-lg sm:text-xl"></i>
                </div>
                <div className="bg-gradient-to-br from-primary-dark/95 via-mystic-dark/90 to-primary-dark/95 border-2 border-primary-gold/30 rounded-3xl px-6 sm:px-8 py-4 sm:py-5 shadow-xl">
                  <div className="flex items-center gap-2">
                    <span className="text-purple-300/60 text-xs mr-2">
                      {sajuResult ? `${sajuResult.dayStem}${sajuResult.dayBranch} 일주 맞춤 분석 중` : '운세 분석 중'}
                    </span>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce"></div>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2.5 h-2.5 bg-primary-gold rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t-2 border-primary-gold/30 p-4 sm:p-6 bg-gradient-to-br from-primary-dark/60 via-mystic-dark/50 to-primary-dark/60 backdrop-blur-lg">
            <div className="flex items-center justify-between text-xs text-mystic-purple mb-3">
              <span>토큰 사용량 (메시지당 8토큰)</span>
              <span className="font-bold text-primary-gold">{usedTokens} / {maxTokens}</span>
            </div>
            <div className="h-1.5 bg-primary-dark/80 rounded-full overflow-hidden border border-primary-gold/20 mb-4">
              <div
                className="h-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((usedTokens / maxTokens) * 100, 100)}%` }}
              ></div>
            </div>

            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="고민하는 결정을 자유롭게 말씀해주세요... (Enter로 전송)"
                  rows={2}
                  className="w-full px-5 sm:px-6 py-3.5 sm:py-4 bg-primary-dark/70 border-2 border-primary-gold/30 rounded-2xl text-white placeholder-gray-400 focus:border-primary-gold focus:shadow-[0_0_20px_rgba(255,215,0,0.25)] focus:outline-none text-sm transition-all duration-300 resize-none custom-scrollbar"
                />
              </div>
              <button
                onClick={handleSend}
                className="w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 text-primary-dark hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] hover:scale-110 transition-all duration-300 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-send-plane-fill text-lg sm:text-xl"></i>
              </button>
            </div>
            <p className="text-center text-purple-300/40 text-xs mt-3">잔여 {tokens} 토큰 · Destiny GPT는 운세 분석 기반 조언입니다</p>
          </div>
        </div>
      </div>

      <InsufficientTokensModal
        isOpen={showInsufficientModal}
        onClose={() => setShowInsufficientModal(false)}
        requiredTokens={requiredTokens}
        currentTokens={tokens}
      />
    </>
  );
}
